import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
} from '@xanectech/ui/components';
import { Mail, User, CheckCircle } from 'lucide-react';
import { useToast } from '@xanectech/ui/lib';
import { supabase } from '@xanectech/supabase-client';
import { createLogger } from '@xanectech/lib';

const logger = createLogger('Auth - WaitlistDialog');

const isWaitlistResponse = (
  value: unknown,
): value is { success?: boolean; error?: string } =>
  !!value && typeof value === 'object';

interface WaitlistDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const WaitlistDialog: React.FC<WaitlistDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const invokeResult: unknown = await supabase.functions.invoke(
        'join-waitlist',
        {
          body: { name, email },
        },
      );
      const data =
        invokeResult &&
        typeof invokeResult === 'object' &&
        'data' in invokeResult
          ? (invokeResult as { data?: unknown }).data
          : undefined;

      if (isWaitlistResponse(data) && data.success) {
        setSubmitted(true);
        toast({
          title: 'Thank you!',
          description:
            "You've been added to our waitlist. Check your email for confirmation.",
        });
      } else {
        const errorMessage: string =
          isWaitlistResponse(data) && typeof data.error === 'string'
            ? data.error
            : 'Failed to join waitlist';
        throw new Error(errorMessage);
      }
    } catch (error) {
      logger.error('Waitlist error:', {
        error: error instanceof Error ? error.message : String(error),
      });
      toast({
        title: 'Error',
        description: 'Failed to join waitlist. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setEmail('');
    setSubmitted(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            {submitted ? (
              <>
                <CheckCircle className='h-5 w-5 text-green-500' />
                You're on the list!
              </>
            ) : (
              <>
                <Mail className='h-5 w-5 text-primary' />
                Join the Waitlist
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {submitted
              ? "We'll send you an email notification as soon as user registration opens."
              : 'The application is currently not open for end user registration. Join our waitlist to be notified when registration becomes available.'}
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className='flex flex-col items-center gap-4 py-4'>
            <CheckCircle className='h-16 w-16 text-green-500' />
            <p className='text-center text-sm text-muted-foreground'>
              Thank you for your interest! We'll contact you at{' '}
              <strong>{email}</strong> when registration opens.
            </p>
            <Button onClick={handleClose} className='w-full'>
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className='space-y-4'>
            <div className='space-y-2'>
              <Label htmlFor='waitlist-name'>Full Name</Label>
              <div className='relative'>
                <User className='absolute left-3 top-3 h-4 w-4 text-muted-foreground' />
                <Input
                  id='waitlist-name'
                  type='text'
                  placeholder='Enter your full name'
                  value={name}
                  onChange={(e) => setName(e.target.value as string)}
                  className='pl-10'
                  required
                />
              </div>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='waitlist-email'>Email Address</Label>
              <div className='relative'>
                <Mail className='absolute left-3 top-3 h-4 w-4 text-muted-foreground' />
                <Input
                  id='waitlist-email'
                  type='email'
                  placeholder='Enter your email address'
                  value={email}
                  onChange={(e) => setEmail(e.target.value as string)}
                  className='pl-10'
                  required
                />
              </div>
            </div>

            <div className='flex gap-2 pt-4'>
              <Button
                type='button'
                variant='outline'
                onClick={handleClose}
                className='flex-1'
              >
                Cancel
              </Button>
              <Button type='submit' disabled={loading} className='flex-1'>
                {loading ? 'Joining...' : 'Join Waitlist'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
